import { useEffect, useMemo } from 'react'
import { FiPlus } from 'react-icons/fi'
import { useDispatch } from 'react-redux'

import { t } from 'i18next'

import { ButtonNavLink } from '~/components/Buttons'
import { DatepickerHeader } from '~/components/datepicker-header'
import HeaderPages from '~/components/HeaderPages'
import { LogoStore } from '~/components/LogoStore'
import PageTitle from '~/components/PageTitle'
import {
  getRetailMediaAccountType,
  getRetailMediaCounterpart,
  isAdvertiser,
  isReadOnly
} from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import BalanceByPublisher from '~/modules/retailMedia/components/BalanceByPublisher'
import { getAccountInfoAction } from '~/modules/retailMedia/store/accountInfo/actions'
import { useAppSelector } from '~/store/hooks'

import * as S from './styles'

const CampaignsPageHeader = ({
  section,
  dashboardTitle,
  showDatepicker = true,
  publisherId
}: CampaignsPageHeaderProps) => {
  const searchParams = useQueryParams()
  // `null` é um publisher resolvido como ausente e não pode cair no `rmid`,
  // senão o header mostraria outro publisher. Por isso o teste é pela presença
  // do prop, e não por `??`, que trataria `null` como "não informado".
  const pageResolvedPublisher = publisherId !== undefined
  const id = pageResolvedPublisher ? publisherId : searchParams.get('rmid')

  const accountType = getRetailMediaAccountType()

  const dispatch = useDispatch()

  const {
    accountInfo: {
      view: { data, loading }
    }
  } = useAppSelector(state => state.retailMedia)

  useEffect(() => {
    if (id) {
      dispatch(
        getAccountInfoAction({
          id,
          params: { account_info: true },
          accountType
        })
      )
    }
  }, [accountType, dispatch, id])

  const pageTitle = useMemo(
    () =>
      id
        ? `${data?.name || t('rm:Loading')} - ${section} - Retail Media`
        : `Retail Media - ${dashboardTitle}`,
    [dashboardTitle, data?.name, id, section]
  )

  const createLinkParam = useMemo(() => (id ? `?rmid=${id}` : ''), [id])

  const insideProps = useMemo(() => {
    if (!id) {
      return {}
    }

    const labelByAccountType = isAdvertiser()
      ? 'publishers'
      : t('rm:advertisers')

    return {
      titleSideRightComponent: (
        <>
          {!loading && (
            <LogoStore
              size="large"
              linkLogo={data?.account_logo}
              alt={data?.name}
              backgroundLogo={
                data?.account_theme?.header_color || 'transparent'
              }
            />
          )}
        </>
      ),
      subtitle: (
        <BalanceByPublisher
          id={id}
          idAccountType={isAdvertiser() ? 'industry' : 'grocery'}
        />
      ),
      labelBreadcrumb: t('rm:BackToDashBoard', { title: labelByAccountType }),
      backTo: `/retail-media/${getRetailMediaCounterpart()}s`
    }
  }, [
    data?.account_logo,
    data?.account_theme?.header_color,
    data?.name,
    id,
    loading
  ])

  return (
    <>
      <PageTitle title={pageTitle} />

      <HeaderPages
        title={id ? data?.name : pageTitle}
        className={showDatepicker ? 'has-datepicker' : undefined}
        sideRightComponent={
          <S.WrapperHeader>
            {isAdvertiser() && !isReadOnly() && (
              <ButtonNavLink
                to={`/retail-media/campaign/create${createLinkParam}`}
                text={t('rm:createCampaign')}
                iconLeft={<FiPlus />}
              />
            )}
            {showDatepicker && <DatepickerHeader />}
          </S.WrapperHeader>
        }
        {...insideProps}
      />
    </>
  )
}

export default CampaignsPageHeader
